import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RemoveInteractionReasonParams, ReqCancelMeetingParams } from '@base/models/requests/cancel-meeting.req';
import { MeetService } from '@base/services/private/meet.service';
import { ModalService } from '@base/services/private/modal.service';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { RequestLabelService } from '@base/services/private/request-label.service';
import { MeetMode } from '@base/enums/meet-params.enum';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { StatusResponse } from '@base/enums/status-response.enum';
import { GlobalStateService } from '@base/services/private/global-state.service';
import { ModalFooterComponent } from '@base/components/private/modals/modal-footer/modal-footer.component';
import { finalize, lastValueFrom, Subscription } from 'rxjs';
import { GlobalService } from '@base/services/private/global.service';
import { ThemeService } from '@base/services/private/theme.service';
import { TypeOfCta } from '@base/enums/schedule.enum';
import { TypeOfRequestLabel } from '@base/enums/request-label.enum';
import { MeetingStatus } from '@base/enums/meet.enum';
import { NotificationService } from '@base/services/notification.service';

// local enum for active button states
enum ActiveButton {
  CANCEL_MEETING = 'cancel-meeting',
}

@Component({
  selector: 'app-modal-cancel-meeting',
  imports: [
    FormsModule,
    NgxSkeletonLoaderComponent,
    ModalHeaderComponent,
    ModalBodyComponent,
    forwardRef(() => TranslatePipe),
    ModalFooterComponent,
  ],
  providers: [forwardRef(() => TranslatePipe)],
  templateUrl: './modal-cancel-meeting.component.html',
  styleUrl: './modal-cancel-meeting.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalCancelMeetingComponent implements OnInit, OnDestroy {
  private readonly modalSrv = inject(ModalService);
  private readonly meetSrv = inject(MeetService);
  private readonly requestLabelSrv = inject(RequestLabelService);
  private readonly globalStateSrv = inject(GlobalStateService);
  private readonly globalSrv = inject(GlobalService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly themeSrv = inject(ThemeService);
  private readonly translatePipe = inject(TranslatePipe);
  private readonly notificationSrv = inject(NotificationService);

  @Input() peopleId = '';
  @Input() modalId = '';
  @Input() mode = MeetMode.INTERNAL;
  @Input() action: TypeOfRequestLabel | string = '';
  @Input() isChatCancellation = false;

  reasonSelected = 0;
  reasons: string[] = [];

  MeetMode = MeetMode;
  ActiveButton = ActiveButton;

  isShowPlaceholder = true;
  isExternalMode = this.globalStateSrv.isExternalMode();
  otherReason = '';
  activeButton = '';

  isBgDarkMode = this.themeSrv.isBgDarkMode;
  isHttpRequestRunning = false;
  httpRequestObserver$: Subscription | null = null;
  errorMessage = '';

  ngOnInit(): void {
    this.getCancelMeetingReason();
    this.listenHttpRequestState();
  }

  ngOnDestroy(): void {
    this.httpRequestObserver$?.unsubscribe();
    this.isHttpRequestRunning = false;
    this.globalSrv.destroyModalRef(this.modalId);
  }

  /**
   * Listens for HTTP request state changes and updates the component state accordingly.
   * @returns void
   */
  listenHttpRequestState(): void {
    this.httpRequestObserver$ = this.globalSrv.httpRequestObserver().subscribe({
      next: (res) => {
        this.isHttpRequestRunning = res;
        this.triggerChangeDetection();
      },
    });
  }

  /**
   * Fetches the list of reasons for canceling a meeting from the server.
   * Updates the reasons list and sets the default selected reason.
   * @returns void
   */
  getCancelMeetingReason(): void {
    const params: RemoveInteractionReasonParams = {
      id: this.peopleId,
      type: 0,
    };
    this.meetSrv.getRemoveInteractionReason(params).subscribe({
      next: (res) => {
        this.reasons = res;
        this.isShowPlaceholder = false;
        if (res.length > 0) {
          this.reasonSelected = 0;
        }
        this.triggerChangeDetection();
      },
      error: (err) => {
        console.error('Error fetching cancel meeting reasons:', err);
        this.isShowPlaceholder = false;
        this.triggerChangeDetection();
      },
    });
  }

  /**
   * Closes the modal with the provided modalId.
   * @returns void
   */
  close(): void {
    this.modalSrv.close(this.modalId, 'close');
  }

  /**
   * Shows a modal warning if the account is a preview account and status response is -2.
   */
  showModalWarning(): void {
    this.modalSrv.showModalWarningForPreviewAccount();
    return;
  }

  /**
   * Sends a request to cancel a meeting for the current person.
   * Uses the selected reason and notifies the system of the update.
   * Executes the callback if provided and closes modals based on the closeAll flag.
   * @returns void
   */
  cancelMeeting(): void {
    this.errorMessage = '';
    this.activeButton = ActiveButton.CANCEL_MEETING;

    if (this.otherReason.trim().length > 100) {
      return;
    }
    const params: ReqCancelMeetingParams = {
      id: this.peopleId,
      reason: this.otherReason.trim().length > 0 ? -1 : this.reasonSelected,
      reason_text: this.otherReason.trim(),
    };
    this.meetSrv
      .reqCancelMeet(params)
      .pipe(
        finalize(() => {
          this.activeButton = '';
          this.triggerChangeDetection();
        })
      )
      .subscribe({
        next: (res) => {
          if (res.status === StatusResponse.SUCCESS) {
            // handle external mode or cancel from sense
            if (this.isExternalMode) {
              this.globalSrv.postMessageToTargets({
                is_success: true,
                message: this.translatePipe.transform('MEETING_CANCELED'),
                description: this.translatePipe.transform('CLOSE_WINDOW'),
                isShowModal: true,
              });
              return;
            }

            this.close();

            (async () => {
              this.doUpdateState();
            })();
          } else if (+res.status === StatusResponse.PREVIEW_ACCOUNT) {
            // Preview account returns status -2, but success returns 'SUCCESS'; always convert to number to check for -2.
            this.showModalWarning();
          }
        },
        error: (err) => {
          console.error('Error canceling meeting:', err);
          this.errorMessage = 'SOMETHING_WENT_WRONG';
        },
      });
  }

  /**
   * Triggers change detection to update the view.
   */
  triggerChangeDetection(): void {
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }

  /**
   *
   * @param event - The change event from the select element.
   * Updates the selected reason based on user selection.
   * @returns void
   */
  onChangeReason(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    this.reasonSelected = parseInt(selectElement.value, 10);
  }

  /**
   * Checks the meeting status for a given person.
   * @param peopleId - The ID of the person to check the meeting status for.
   * @returns
   */
  async getCheckMeeting(peopleId: string) {
    try {
      const data = await lastValueFrom(this.meetSrv.getCheckMeeting({ id: peopleId }));
      return data;
    } catch (error) {
      console.error('Error checking meeting status:', error);
      throw error;
    }
  }

  /**
   * Gets the meeting status and updates the request label service.
   * @returns void
   */
  async doUpdateState(): Promise<void> {
    const meetingData = await this.getCheckMeeting(this.peopleId);
    const meetingStatus = meetingData?.meeting_status;
    if (!meetingData) return;
    const data = {
      status: true,
      data: {
        id: this.peopleId,
        action: this.action,
        newValue: {
          available_cta:
            meetingStatus === MeetingStatus.INITIATED_TO_MEET
              ? [TypeOfCta.MEET, TypeOfCta.ARCHIVE]
              : [TypeOfCta.ACCEPT_MEET, TypeOfCta.DECLINE],
          floorplan: null,
          meeting_info: null,
          meeting_link: null,
          meeting_local_info: null,
          object_loc: null,
          meeting_status: meetingData.meeting_status,
        },
      },
    };
    this.requestLabelSrv.setEventUpdated(data);
    this.getMeetNotifData();
  }

  /**
   * Fetches meeting notification data and updates the global state.
   * @returns void
   */
  getMeetNotifData(): void {
    this.notificationSrv?.getMeetNotifData().subscribe({
      next: (res) => {
        this.globalStateSrv.setState({
          meetNotif: res,
        });
      },
      error: (err) => {
        console.error('Error fetching meeting notification data:', err);
      },
    });
  }
}
